import { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, ArrowUp, ArrowDown, Save, Megaphone, Eye, EyeOff, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminApi';

const inputCls = 'border border-[#eaeaea] focus:border-[#eb5381] outline-none px-3 py-2 text-sm w-full';
const newId = () => (crypto?.randomUUID?.() || `a_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);

const blank = () => ({ id: newId(), text: '', link: '', start: '', end: '', published: true });

const isActive = (a) => {
  if (!a.published || !(a.text || '').trim()) return false;
  const today = new Date().toISOString().slice(0, 10);
  if (a.start && today < a.start) return false;
  if (a.end && today > a.end) return false;
  return true;
};

export default function AdminAnnouncements() {
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    adminApi.getAnnouncements()
      .then(d => setItems((Array.isArray(d) ? d : []).map(a => ({ ...blank(), ...a }))))
      .catch(() => { toast.error('Could not load announcements'); setItems([]); });
  }, []);

  const mutate = (next) => { setItems(next); setDirty(true); };
  const update = (i, patch) => mutate(items.map((a, x) => x === i ? { ...a, ...patch } : a));
  const add = () => mutate([...items, blank()]);
  const remove = (i) => mutate(items.filter((_, x) => x !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    mutate(next);
  };

  const save = async () => {
    const cleaned = items.filter(a => (a.text || '').trim());
    setSaving(true);
    try {
      await adminApi.saveAnnouncements(cleaned);
      setItems(cleaned.length ? cleaned : items);
      setDirty(false);
      toast.success('Announcements saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  if (!items) return <div className="py-16 flex justify-center text-neutral-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const activeOnes = items.filter(isActive);

  return (
    <div data-testid="admin-announcements-page" className="max-w-4xl">
      <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381]">Homepage</div>
      <div className="mt-1 flex items-center gap-3 flex-wrap">
        <h1 className="font-serif text-3xl md:text-4xl flex items-center gap-3"><Megaphone className="w-7 h-7 text-[#eb5381]" /> Announcements</h1>
        <div className="flex-1" />
        <button onClick={add} data-testid="add-announcement-btn" className="inline-flex items-center gap-1 border border-[#eaeaea] hover:border-[#eb5381] px-4 py-2 text-[10px] uppercase tracking-[0.2em]"><Plus className="w-3.5 h-3.5" /> Add</button>
        <button onClick={save} disabled={saving || !dirty} data-testid="save-announcements-btn" className="inline-flex items-center gap-1 bg-[#eb5381] disabled:opacity-40 text-white px-5 py-2 text-[10px] uppercase tracking-[0.2em]">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
        </button>
      </div>
      <p className="mt-2 text-sm text-neutral-600">A scrolling strip shown at the very top of every page. Only published announcements within their date window appear live. The strip hides entirely when nothing is active.</p>

      {/* Live preview */}
      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 mb-2">Live preview</div>
        {activeOnes.length ? (
          <div className="h-9 bg-[#eb5381] overflow-hidden flex items-center rounded-sm" data-testid="announcement-preview">
            <div className="flex items-center gap-6 px-4 whitespace-nowrap overflow-hidden">
              {activeOnes.map(a => (
                <span key={a.id} className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white">
                  {a.text}{a.link && <Link2 className="w-3 h-3 opacity-70" />}
                  <span className="text-white/40">✦</span>
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div className="h-9 border border-dashed border-neutral-300 bg-neutral-50 flex items-center justify-center text-[11px] uppercase tracking-[0.2em] text-neutral-400 rounded-sm">Strip hidden — no active announcements</div>
        )}
      </div>

      {/* Editor list */}
      <div className="mt-8 space-y-4">
        {items.length === 0 && (
          <div className="border border-dashed border-neutral-300 p-10 text-center text-sm text-neutral-500">
            No announcements yet. Click <span className="font-medium">Add</span> to create one.
          </div>
        )}
        {items.map((a, i) => (
          <div key={a.id} data-testid={`announcement-${i}`} className={`border p-4 ${a.published ? 'border-[#eaeaea]' : 'border-dashed border-neutral-300 bg-neutral-50'}`}>
            <div className="flex items-start gap-3">
              <div className="flex flex-col gap-1 pt-1">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="text-neutral-400 hover:text-[#eb5381] disabled:opacity-30" data-testid={`move-up-${i}`}><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-neutral-400 hover:text-[#eb5381] disabled:opacity-30" data-testid={`move-down-${i}`}><ArrowDown className="w-4 h-4" /></button>
              </div>
              <div className="flex-1 space-y-3">
                <input className={inputCls} placeholder="Announcement text" value={a.text} onChange={e => update(i, { text: e.target.value })} data-testid={`announcement-text-${i}`} />
                <input className={inputCls} placeholder="Link (optional) — /artists or https://…" value={a.link} onChange={e => update(i, { link: e.target.value })} data-testid={`announcement-link-${i}`} />
                <div className="flex flex-wrap gap-4 items-end">
                  <label className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                    Start <span className="normal-case tracking-normal text-neutral-400">(optional)</span>
                    <input type="date" className={`${inputCls} mt-1`} value={a.start || ''} onChange={e => update(i, { start: e.target.value })} data-testid={`announcement-start-${i}`} />
                  </label>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                    End <span className="normal-case tracking-normal text-neutral-400">(optional)</span>
                    <input type="date" className={`${inputCls} mt-1`} value={a.end || ''} onChange={e => update(i, { end: e.target.value })} data-testid={`announcement-end-${i}`} />
                  </label>
                  <div className="flex-1" />
                  <button onClick={() => update(i, { published: !a.published })} data-testid={`announcement-publish-${i}`} className={`inline-flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-[0.2em] border ${a.published ? 'border-[#eb5381] text-[#eb5381] bg-[#fff5f8]' : 'border-neutral-300 text-neutral-500'}`}>
                    {a.published ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} {a.published ? 'Published' : 'Draft'}
                  </button>
                  <button onClick={() => remove(i)} data-testid={`announcement-delete-${i}`} className="text-neutral-400 hover:text-red-500 p-2"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
